import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../hooks/useAuth';
import { getFriendlyAuthError } from '../utils/authErrorMessages';
import { summarizeAccountDeletion } from '../services/settingsService';

import DeleteAccountModal from '../components/settings/DeleteAccountModal';
import SettingsSwitch from '../components/settings/SettingsSwitch';
import SegmentedControl from '../components/settings/SegmentedControl';
import ErrorModal from '../components/ErrorModal';
import ResetConfirmModal from '../components/ResetConfirmModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { Button } from '../components/ui';
import { errorDev } from '../utils/logger.js';
import MeDashboardFooterPanel from '../components/me/MeDashboardFooterPanel';
import OrrithFeedbackSystem from '../components/OrrithFeedbackSystem';

import {
  ArrowLeft,
  Download,
  RotateCcw,
  LogOut,
  Trash2,
  ShieldCheck,
  MailCheck,
  Pencil,
} from 'lucide-react';

const PRIVACY_FIELDS = ['email', 'phone', 'avatar', 'location', 'bio'];

// Section definitions live at module scope so the scroll-spy
// observer can read stable ids without depending on any
// component value. Labels are still resolved per render
// through safeT so they follow the active language.
const SETTINGS_SECTIONS = [
  { id: 'general', labelKey: 'controlPanel.navigation.general', fallback: 'General' },
  { id: 'privacy', labelKey: 'controlPanel.navigation.privacy', fallback: 'Privacy & data' },
  { id: 'security', labelKey: 'controlPanel.navigation.security', fallback: 'Security' },
  { id: 'danger-zone', labelKey: 'controlPanel.navigation.danger', fallback: 'Danger zone' },
];

function SettingsSection({ id, title, description, children, sectionRef }) {
  return (
    <section
      id={id}
      ref={sectionRef}
      tabIndex={-1}
      aria-label={title}
      className="scroll-mt-24 rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-black shadow-sm focus-visible:outline-none"
    >
      <div className="px-4 sm:px-6 pt-4 pb-3">
        <h2 className="text-base font-bold text-gray-900 dark:text-white">{title}</h2>
        {description ? (
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">{description}</p>
        ) : null}
      </div>
      <div className="px-4 sm:px-6 pb-3 divide-y divide-gray-100 dark:divide-neutral-800/70">
        {children}
      </div>
    </section>
  );
}

function SettingsRow({ label, description, children }) {
  return (
    <div className="flex flex-col gap-2.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
        {description ? (
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{description}</p>
        ) : null}
      </div>
      <div className="w-full sm:w-auto sm:min-w-[220px]">{children}</div>
    </div>
  );
}

function DangerAction({ label, description, icon: Icon, actionLabel, onClick, disabled, variant = 'secondary', busy = false }) {
  return (
    <div className="flex flex-col gap-2.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="flex min-w-0 items-start gap-2.5">
        {Icon ? <Icon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" /> : null}
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
          {description ? (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">{description}</p>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        onClick={onClick}
        disabled={disabled || busy}
        variant={variant}
        size="sm"
        className="shrink-0"
      >
        {actionLabel}
      </Button>
    </div>
  );
}

export default function Settings() {
  const {
    settings,
    loading,
    saving,
    updateSetting,
    exportData,
    deleteAccount,
    resetToDefaults,
  } = useSettings();

  const { t, i18n, ready: i18nReady } = useTranslation(['settings', 'auth', 'common']);
  const { sendVerificationEmail, refreshVerificationStatus, logout } = useAuth();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState('general');
  const [exporting, setExporting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [exportError, setExportError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deletionSummary, setDeletionSummary] = useState(null);
  const [resetError, setResetError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState({ type: '', message: '' });
  const [resendCooldown, setResendCooldown] = useState(0);

  const sectionRefs = useRef({});

  const safeT = useCallback(
    (key, fallback = key, ns = undefined) => {
      if (!i18nReady) return fallback;
      try {
        const result = ns ? t(key, { ns }) : t(key);
        return result === key ? fallback : result;
      } catch (err) {
        errorDev("Operation failed:", err);
        return fallback;
      }
    },
    [i18nReady, t]
  );

  const languageOptions = useMemo(
    () => [
      { value: 'en', label: safeT('controlPanel.languages.english', 'English') },
      { value: 'rw', label: safeT('controlPanel.languages.kinyarwanda', 'Kinyarwanda') },
    ],
    [safeT]
  );

  const themeOptions = useMemo(
    () => [
      { value: 'light', label: safeT('theme.light', 'Light') },
      { value: 'dark', label: safeT('theme.dark', 'Dark') },
      { value: 'system', label: safeT('theme.system', 'System') },
    ],
    [safeT]
  );

  const sectionLinks = SETTINGS_SECTIONS.map((section) => ({
    id: section.id,
    label: safeT(section.labelKey, section.fallback),
  }));

  // Scroll-spy so the navigation reflects the section in view.
  // The section ids come from a module constant, so this effect
  // has no component dependencies and only needs to run once.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
    );

    SETTINGS_SECTIONS.forEach((section) => {
      const node = document.getElementById(section.id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const node = document.getElementById(id);
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    sectionRefs.current[id]?.focus?.();
  };

  // Deep links such as /settings#privacy scroll to their section once
  // the loading gate lifts and the section exists in the DOM.
  useEffect(() => {
    if (loading || !settings) return undefined;
    const hash = (window.location.hash || '').replace('#', '');
    if (!hash) return undefined;
    const node = document.getElementById(hash);
    if (!node) return undefined;
    const raf = window.requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [loading, settings]);

  // Immediate, persisted preference changes. Each handler applies the
  // change optimistically through the canonical settings service and
  // reconciles with the stored value on failure.
  const handleLanguageChange = useCallback(
    async (value) => {
      setSaveError(null);
      try {
        await updateSetting('language', value);
        await i18n.changeLanguage(value);
      } catch (err) {
        errorDev("Operation failed:", err);
        setSaveError(
          safeT('controlPanel.errors.updateMessage', 'We could not save that change. It has been restored.')
        );
      }
    },
    [updateSetting, i18n, safeT]
  );

  const handleThemeChange = useCallback(
    async (value) => {
      setSaveError(null);
      try {
        // useTheme remains the sole owner of theme application; the
        // settings service persists the selection to Firestore.
        await updateSetting('theme', value);
      } catch (err) {
        errorDev("Operation failed:", err);
        setSaveError(
          safeT('controlPanel.errors.updateMessage', 'We could not save that change. It has been restored.')
        );
      }
    },
    [updateSetting, safeT]
  );

  const handleProfileVisibilityChange = useCallback(
    async (value) => {
      setSaveError(null);
      try {
        await updateSetting('profilePublic', value);
      } catch (err) {
        errorDev("Operation failed:", err);
        setSaveError(
          safeT('controlPanel.errors.updateMessage', 'We could not save that change. It has been restored.')
        );
      }
    },
    [updateSetting, safeT]
  );

  const handleFieldVisibilityChange = useCallback(
    async (field, value) => {
      setSaveError(null);
      try {
        // The updater resolves against the latest stored value, so
        // rapid toggles of several fields never lose an intermediate
        // change.
        await updateSetting('publicFields', (prev) => ({ ...(prev || {}), [field]: value }));
      } catch (err) {
        errorDev("Operation failed:", err);
        setSaveError(
          safeT('controlPanel.errors.updateMessage', 'We could not save that change. It has been restored.')
        );
      }
    },
    [updateSetting, safeT]
  );

  const handleResendVerificationEmail = async () => {
    try {
      setVerifyBusy(true);
      setVerifyStatus({ type: '', message: '' });
      await sendVerificationEmail();
      setResendCooldown(60);
      setVerifyStatus({
        type: 'success',
        message: safeT('auth.verificationEmailSent', 'Verification email sent. Please check your inbox and spam folder.', 'auth'),
      });
    } catch (error) {
      setVerifyStatus({
        type: 'error',
        message:
          getFriendlyAuthError(error, t, {
            defaultKey: 'auth.errors.verificationEmailFailed',
            defaultMessage: 'We could not send the verification email right now. Please try again.',
          }) || safeT('auth.errors.verificationEmailFailed', 'Failed to send verification email. Please try again.', 'auth'),
      });
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleRefreshVerificationStatus = async () => {
    try {
      setVerifyBusy(true);
      setVerifyStatus({ type: '', message: '' });
      const isVerified = await refreshVerificationStatus();
      setVerifyStatus({
        type: isVerified ? 'success' : 'error',
        message: isVerified
          ? safeT('auth.emailVerifiedSuccess', 'Your email has been verified successfully.', 'auth')
          : safeT('auth.emailStillUnverified', 'Email is still unverified. Complete verification from your inbox, then refresh.', 'auth'),
      });
    } catch (error) {
      setVerifyStatus({
        type: 'error',
        message:
          getFriendlyAuthError(error, t, {
            defaultKey: 'auth.errors.authError',
            defaultMessage: 'We could not refresh your verification status right now. Please try again.',
          }) || safeT('auth.errors.authError', 'An authentication error occurred. Please try again.', 'auth'),
      });
    } finally {
      setVerifyBusy(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      errorDev("Operation failed:", err);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setExportError(null);
      await exportData();
    } catch (error) {
      setExportError({
        title: safeT('data.exportError', 'Export failed'),
        message: safeT('controlPanel.errors.exportMessage', 'Unable to export your data right now. Please try again.'),
        error,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      setDeleteError(null);

      // The service reports what it deleted and what client rules
      // refused to delete. That report is what the user is shown,
      // instead of an unqualified "everything was deleted" claim.
      const result = await deleteAccount();
      const summary = summarizeAccountDeletion(result);

      localStorage.clear();
      sessionStorage.clear();

      setShowDeleteModal(false);
      setDeletionSummary(summary);
    } catch (error) {
      let title = safeT('deleteAccount.error', 'Failed to delete account');
      let message = safeT('deleteAccount.errorDetails', 'Your account could not be deleted right now. Please try again.');

      if (error?.code === 'auth/requires-recent-login') {
        title = safeT('deleteAccount.errorTitle', 'Authentication required');
        message = safeT('deleteAccount.recentAuthRequired', 'For security, sign out and sign in again before deleting your account.');
      } else if (error?.code === 'auth/user-token-expired') {
        title = safeT('deleteAccount.errorTitle', 'Session expired');
        message = safeT('deleteAccount.sessionExpired', 'Your session expired. Sign in again and retry deletion.');
      } else {
        message = getFriendlyAuthError(error, t, {
          defaultKey: 'deleteAccount.errorDetails',
          defaultMessage: 'Your account could not be deleted right now. Please try again.',
        });
      }

      setDeleteError({ title, message, error });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmReset = async () => {
    try {
      setResetError(null);
      await resetToDefaults();
      setShowResetConfirm(false);
    } catch (error) {
      setResetError({
        title: safeT('data.resetError', 'Reset failed'),
        message: safeT('data.resetErrorDetails', 'Unable to reset settings right now. Please try again.'),
        error,
      });
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = window.setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  if ((loading && !settings)) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-4">
        <LoadingSpinner size="large" text={safeT('common:loading', 'Loading...')} />
      </div>
    );
  }

  // Deleting the auth account clears `user`, which would otherwise drop the user
  // straight into the sign-in panel. The receipt is therefore rendered first so
  // an incomplete deletion is stated before the session ends.
  if (deletionSummary) {
    const { deleted, retained, incomplete } = deletionSummary;

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-2xl shadow-sm p-5 sm:p-6 text-left">
          <div className="flex items-center gap-3 mb-3">
            <span className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
              <ShieldCheck className="text-green-700 dark:text-green-400" size={22} />
            </span>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {safeT('deleteAccountResult.title', 'Account deleted')}
            </h2>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400">
            {safeT(
              'deleteAccountResult.summary',
              'Your Orrith account has been closed and your sign-in removed.'
            )}
          </p>

          <dl className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 p-3">
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {safeT('deleteAccountResult.deletedLabel', 'Records removed')}
              </dt>
              <dd className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{deleted}</dd>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 p-3">
              <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {safeT('deleteAccountResult.retainedLabel', 'Records retained')}
              </dt>
              <dd className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{retained}</dd>
            </div>
          </dl>

          {incomplete && (
            <div className="mt-4 p-3 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20">
              <p className="text-sm text-amber-900 dark:text-amber-100">
                {safeT(
                  'deleteAccountResult.retainedNotice',
                  'Some records that mention you were kept because they belong to shared marketplace and support history. They are no longer linked to a sign-in, and keeping them does not let anyone sign in as you.'
                )}
              </p>
            </div>
          )}

          <Button
            onClick={() => {
              setDeletionSummary(null);
              navigate('/');
            }}
            variant="primary"
            className="w-full mt-5 py-2.5 text-sm"
          >
            {safeT('deleteAccountResult.continue', 'Continue')}
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-2xl shadow-sm p-5 sm:p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{safeT('signInRequired', 'Sign In Required')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">{safeT('controlPanel.authRequiredMessage', 'Please sign in to access your settings.')}</p>
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              className="w-full py-2.5 text-sm"
            >
              {safeT('signIn', 'Sign In', 'auth')}
            </Button>
            <Button
              onClick={() => navigate('/signup')}
              variant="secondary"
              className="w-full py-2.5 text-sm"
            >
              {safeT('signUp', 'Sign Up', 'auth')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const emailVerified = Boolean(user?.emailVerified);
  const publicFields = settings?.publicFields || {};

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black pb-20 sm:pb-24">
      <div className="border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-black sticky top-0 z-20">
        <div className="max-w-6xl mx-auto flex h-12 items-center gap-2 px-3 sm:px-6">
          <Link
            to="/me"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={safeT('controlPanel.backToProfile', 'Profile')}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </Link>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            {safeT('title', 'Settings')}
          </h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 pt-5 sm:px-6">
        <p className="text-sm text-gray-600 dark:text-gray-400">{safeT('description', 'Manage your account preferences and privacy')}</p>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 sm:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-4 sm:gap-5">
          <aside className="lg:sticky lg:top-28 self-start" aria-label={safeT('title', 'Settings')}>
            <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-black p-2 shadow-sm">
              <div className="flex gap-1.5 overflow-x-auto lg:block lg:space-y-1 lg:overflow-visible pb-1 lg:pb-0 no-scrollbar" role="navigation" aria-label={safeT('controlPanel.navigation.title', 'Settings sections')}>
                {sectionLinks.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    aria-current={activeSection === item.id ? 'true' : undefined}
                    className={`shrink-0 lg:w-full rounded-lg px-3 lg:px-2.5 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 ${
                      activeSection === item.id
                        ? 'bg-green-50 dark:bg-green-900/20 font-semibold text-green-800 dark:text-green-200'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-900'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="space-y-4 sm:space-y-5">
            {saveError && (
              <div role="alert" className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-start justify-between gap-3">
                <p className="text-sm text-red-700 dark:text-red-200">{saveError}</p>
                <button
                  type="button"
                  onClick={() => setSaveError(null)}
                  className="text-red-500 hover:text-red-700 dark:hover:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 rounded"
                  aria-label={safeT('common:close', 'Close')}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            )}

            <SettingsSection
              id="general"
              title={safeT('controlPanel.general.title', 'General')}
              description={safeT('controlPanel.general.description', 'Core interface preferences')}
              sectionRef={(el) => { sectionRefs.current.general = el; }}
            >
              <SettingsRow
                label={safeT('language', 'Language')}
                description={safeT('languageDescription', 'Select your preferred language')}
              >
                <SegmentedControl
                  name="settings-language"
                  label={safeT('language', 'Language')}
                  options={languageOptions}
                  value={settings?.language || 'en'}
                  onChange={handleLanguageChange}
                  disabled={saving}
                />
              </SettingsRow>

              <SettingsRow
                label={safeT('theme.title', 'Theme')}
                description={safeT('theme.description', 'Choose your preferred theme')}
              >
                <SegmentedControl
                  name="settings-theme"
                  label={safeT('theme.title', 'Theme')}
                  options={themeOptions}
                  value={settings?.theme || 'system'}
                  onChange={handleThemeChange}
                  disabled={saving}
                />
              </SettingsRow>

              <div className="sm:hidden -mx-4 sm:mx-0 px-4">
                <SettingsRow
                  label={safeT('controlPanel.cookies.title', 'Cookie settings')}
                  description={safeT('controlPanel.cookies.description', 'Review storage permissions for this device.')}
                >
                  <Button
                    type="button"
                    onClick={() => window.dispatchEvent(new CustomEvent('cookie-banner-open-settings'))}
                    variant="secondary"
                    className="w-full px-4 py-2.5 text-sm border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  >
                    {safeT('controlPanel.cookies.button', 'Open cookie settings')}
                  </Button>
                </SettingsRow>
              </div>
            </SettingsSection>

            <SettingsSection
              id="privacy"
              title={safeT('controlPanel.privacy.title', 'Privacy & data')}
              description={safeT('controlPanel.privacy.description', 'Control your profile visibility and your data')}
              sectionRef={(el) => { sectionRefs.current.privacy = el; }}
            >
              <SettingsSwitch
                id="settings-profile-visibility"
                label={safeT('controlPanel.privacy.profileVisibility', 'Public profile')}
                description={safeT('controlPanel.privacy.profileVisibilityDescription', 'When off, only you can see your profile')}
                checked={settings?.profilePublic !== false}
                onChange={handleProfileVisibilityChange}
                disabled={saving}
                onLabel={safeT('switchState.on', 'On')}
                offLabel={safeT('switchState.off', 'Off')}
              />

              <div className="py-2">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  {safeT('controlPanel.privacy.fieldVisibility', 'Profile field visibility')}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                  {safeT('controlPanel.privacy.fieldVisibilityDescription', 'Choose which profile details are visible to others')}
                </p>
                <div className="mt-1 divide-y divide-gray-100 dark:divide-neutral-800/70">
                  {PRIVACY_FIELDS.map((field) => (
                    <SettingsSwitch
                      key={field}
                      id={`settings-field-${field}`}
                      label={safeT(`controlPanel.privacy.fields.${field}`, field)}
                      description={safeT(`controlPanel.privacy.fieldDescriptions.${field}`, '')}
                      checked={publicFields[field] !== false}
                      onChange={(value) => handleFieldVisibilityChange(field, value)}
                      disabled={saving}
                      onLabel={safeT('switchState.on', 'On')}
                      offLabel={safeT('switchState.off', 'Off')}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
                <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Download className="w-4 h-4 text-green-700 dark:text-green-400" aria-hidden="true" />
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{safeT('controlPanel.privacy.exportTitle', 'Export data')}</p>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    {safeT('controlPanel.privacy.exportDescription', 'Download your profile, products, offers, notifications, ratings, support tickets, and wishlist records.')}
                  </p>
                  <Button
                    onClick={handleExport}
                    disabled={exporting}
                    variant="primary"
                    className="w-full px-4 py-2.5 text-sm"
                  >
                    {exporting ? safeT('exporting', 'Exporting...', 'common') : safeT('data.exportButton', 'Export Data')}
                  </Button>
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-neutral-800 bg-gray-50 dark:bg-neutral-900 p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <RotateCcw className="w-4 h-4 text-blue-700 dark:text-blue-400" aria-hidden="true" />
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{safeT('controlPanel.privacy.resetTitle', 'Reset preferences')}</p>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    {safeT('controlPanel.privacy.resetDescription', 'Reset language, theme, and settings fields to the default baseline.')}
                  </p>
                  <Button
                    onClick={() => setShowResetConfirm(true)}
                    disabled={saving}
                    variant="secondary"
                    className="w-full px-4 py-2.5 text-sm border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                  >
                    {saving ? safeT('resetting', 'Resetting...', 'common') : safeT('data.resetButton', 'Reset to Defaults')}
                  </Button>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              id="security"
              title={safeT('controlPanel.security.title', 'Security')}
              description={safeT('controlPanel.security.description', 'Keep your account secure and manage your session')}
              sectionRef={(el) => { sectionRefs.current.security = el; }}
            >
              <div className="py-3">
                <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {safeT('controlPanel.security.emailStatus', 'Email verification')}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {safeT('controlPanel.security.emailStatusDescription', 'A verified email protects your account and unlocks all features')}
                    </p>
                  </div>
                  <span
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                      emailVerified
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                    }`}
                  >
                    {emailVerified ? safeT('controlPanel.security.verified', 'Verified') : safeT('controlPanel.security.unverified', 'Not verified')}
                  </span>
                </div>

                {!emailVerified && (
                  <div className="mt-3 space-y-3">
                    {verifyStatus.message && (
                      <p
                        role="status"
                        className={`text-xs ${verifyStatus.type === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}
                      >
                        {verifyStatus.message}
                      </p>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleResendVerificationEmail}
                        disabled={verifyBusy || resendCooldown > 0}
                        className="px-4 py-2.5 text-sm"
                      >
                        {resendCooldown > 0
                          ? `${safeT('auth.resendIn', 'Resend in', 'auth')} ${resendCooldown}s`
                          : safeT('auth.resendVerificationEmail', 'Resend verification email', 'auth')}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleRefreshVerificationStatus}
                        disabled={verifyBusy}
                        className="px-4 py-2.5 text-sm"
                      >
                        {safeT('auth.refreshVerificationStatus', 'Refresh verification status', 'auth')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/edit-profile"
                className="flex items-center justify-between gap-4 py-3 group"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-green-700 dark:group-hover:text-green-300">
                      {safeT('controlPanel.security.editProfile', 'Edit profile')}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      {safeT('controlPanel.security.editProfileDescription', 'Update your name, photo, and contact details')}
                    </p>
                  </div>
                </div>
                <ArrowLeft className="h-4 w-4 rotate-180 text-gray-400 group-hover:text-green-600" aria-hidden="true" />
              </Link>
            </SettingsSection>

            <SettingsSection
              id="danger-zone"
              title={safeT('controlPanel.danger.title', 'Danger zone')}
              description={safeT('controlPanel.danger.description', 'High-impact actions with immediate account consequences')}
              sectionRef={(el) => { sectionRefs.current['danger-zone'] = el; }}
            >
              <DangerAction
                label={safeT('controlPanel.danger.signOut', 'Sign out')}
                description={safeT('controlPanel.danger.signOutDescription', 'End your current session on this device')}
                icon={LogOut}
                actionLabel={safeT('signOutButton', 'Sign Out')}
                onClick={handleSignOut}
                variant="secondary"
              />

              <DangerAction
                label={safeT('controlPanel.danger.deleteTitle', 'Delete account')}
                description={safeT('controlPanel.danger.deleteDescription', 'Permanently close your account and remove your data')}
                icon={Trash2}
                actionLabel={safeT('deleteAccountButton', 'Delete Account')}
                onClick={() => setShowDeleteModal(true)}
                disabled={isDeleting}
                variant="danger"
                busy={isDeleting}
              />
            </SettingsSection>

            <section aria-label={safeT('feedback.title', 'Feedback')} className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-black shadow-sm p-4 sm:p-6">
              <OrrithFeedbackSystem mode="inline" />
            </section>
          </main>
        </div>

        <div className="mx-auto max-w-6xl px-3 sm:px-6">
          <MeDashboardFooterPanel />
        </div>
      </div>

      {showDeleteModal && (
        <DeleteAccountModal
          onConfirm={handleDeleteAccount}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}

      {showResetConfirm && (
        <ResetConfirmModal
          onConfirm={handleConfirmReset}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}

      {exportError && (
        <ErrorModal
          title={exportError.title}
          message={exportError.message}
          error={exportError.error}
          isDangerous={false}
          onClose={() => setExportError(null)}
          actionLabel={safeT('retry', 'Retry', 'common')}
          onAction={handleExport}
        />
      )}

      {deleteError && (
        <ErrorModal
          title={deleteError.title}
          message={deleteError.message}
          error={deleteError.error}
          isDangerous={true}
          onClose={() => setDeleteError(null)}
          actionLabel={
            deleteError.error?.code === 'auth/requires-recent-login' ||
            deleteError.error?.code === 'auth/user-token-expired'
              ? safeT('signOut', 'Sign Out', 'auth')
              : safeT('retry', 'Retry', 'common')
          }
          onAction={
            deleteError.error?.code === 'auth/requires-recent-login' ||
            deleteError.error?.code === 'auth/user-token-expired'
              ? handleSignOut
              : handleDeleteAccount
          }
        />
      )}

      {resetError && (
        <ErrorModal
          title={resetError.title}
          message={resetError.message}
          error={resetError.error}
          isDangerous={true}
          onClose={() => setResetError(null)}
        />
      )}
    </div>
  );
}
